import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandCompetitorsApi, brandsApi, type BrandInput } from '@/api/brands';
import type { PaginationParams } from '@/types';

export const brandKeys = {
  all: ['brands'] as const,
  list: (params: PaginationParams = {}) => [...brandKeys.all, 'list', params] as const,
  detail: (id: number) => [...brandKeys.all, 'detail', id] as const,
};

export function useBrands(params: PaginationParams = {}) {
  return useQuery({
    queryKey: brandKeys.list(params),
    queryFn: () => brandsApi.list(params),
  });
}

export function useBrand(id: number | undefined) {
  return useQuery({
    queryKey: brandKeys.detail(id ?? 0),
    queryFn: () => brandsApi.getById(id as number),
    enabled: id !== undefined,
  });
}

export function useCreateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandInput) => brandsApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: brandKeys.all }),
  });
}

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: BrandInput }) => brandsApi.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: brandKeys.all }),
  });
}

export function useDeleteBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => brandsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: brandKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Brand competitors
// ---------------------------------------------------------------------------

export const brandCompetitorKeys = {
  all: (brandId: number) => [...brandKeys.all, brandId, 'competitors'] as const,
  ids: (brandId: number) => [...brandKeys.all, brandId, 'competitors', 'ids'] as const,
};

/** Full competitor list (with brand details) for a brand. */
export function useBrandCompetitors(brandId: number | undefined) {
  return useQuery({
    queryKey: brandCompetitorKeys.all(brandId ?? 0),
    queryFn: () => brandCompetitorsApi.list(brandId as number),
    enabled: brandId !== undefined,
  });
}

/** Competitor brand ids only — used to pre-populate competitor pickers. */
export function useBrandCompetitorIds(brandId: number | undefined) {
  return useQuery({
    queryKey: brandCompetitorKeys.ids(brandId ?? 0),
    queryFn: () => brandCompetitorsApi.listIds(brandId as number),
    enabled: brandId !== undefined,
  });
}

/** Admin-only: replace a brand's competitor set in one call. */
export function useSetBrandCompetitors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      brandId,
      competitorBrandIds,
      defaultCompetitorBrandId,
    }: {
      brandId: number;
      competitorBrandIds: number[];
      defaultCompetitorBrandId?: number;
    }) => brandCompetitorsApi.set(brandId, competitorBrandIds, defaultCompetitorBrandId),
    onSuccess: (_data, { brandId }) => {
      // `all(brandId)` is a prefix of `ids(brandId)`, so this invalidates both.
      queryClient.invalidateQueries({ queryKey: brandCompetitorKeys.all(brandId) });
    },
  });
}

/** Admin-only: add a single competitor. */
export function useAddBrandCompetitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, competitorBrandId }: { brandId: number; competitorBrandId: number }) =>
      brandCompetitorsApi.add(brandId, competitorBrandId),
    onSuccess: (_data, { brandId }) =>
      queryClient.invalidateQueries({ queryKey: brandCompetitorKeys.all(brandId) }),
  });
}

/** Admin-only: remove a single competitor. */
export function useRemoveBrandCompetitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, competitorBrandId }: { brandId: number; competitorBrandId: number }) =>
      brandCompetitorsApi.remove(brandId, competitorBrandId),
    onSuccess: (_data, { brandId }) =>
      queryClient.invalidateQueries({ queryKey: brandCompetitorKeys.all(brandId) }),
  });
}
